import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { UserInfo } from '../models/user/user-info.model';
import { ApiService } from './api.service';

const TOKEN_KEY = 'auth-token';
const REFRESHTOKEN_KEY = 'auth-refreshtoken';
const USER_KEY = 'auth-user';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private static _isAdmin = false;
  private static _checkRequest?: Observable<UserInfo | undefined>;
  private static _refreshRequest?: Observable<{ token: string, refreshToken: string }>;

  public userRoles = {
    'Admin': 'Admin',
    'Customer': 'Customer'
  };

  constructor(private mApiService: ApiService) { }

  signOut(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESHTOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  public saveToken(token: string): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.setItem(TOKEN_KEY, token);
  }

  public getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  public saveRefreshToken(token: string): void {
    localStorage.removeItem(REFRESHTOKEN_KEY);
    localStorage.setItem(REFRESHTOKEN_KEY, token);
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem(REFRESHTOKEN_KEY);
  }

  public saveUser(token: string): void {
    localStorage.removeItem(USER_KEY);
    if (token) {
      const userData = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
      localStorage.setItem(USER_KEY, JSON.stringify({
        id: userData['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
        role: userData['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
        userName: userData['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
        expiry: userData.exp
      } as UserInfo));
    }
  }

  public getUser(): Observable<UserInfo | undefined> {
    const user = localStorage.getItem(USER_KEY);
    if (user) {
      let parsedUser = JSON.parse(user) as UserInfo;

      if (parsedUser.expiry * 1000 <= new Date().getTime()) {
        const currentRefreshToken = this.getRefreshToken();

        if (currentRefreshToken) {
          if (UserService._checkRequest) {
            return UserService._checkRequest;
          }

          UserService._checkRequest = new Observable<UserInfo | undefined>(obsr => {
            this.refreshToken(currentRefreshToken).subscribe({
              next: r => {
                UserService._checkRequest = undefined;
                this.saveRefreshToken(r.refreshToken);
                this.saveToken(r.token);
                this.saveUser(r.token);
                parsedUser = JSON.parse(localStorage.getItem(USER_KEY) as string) as UserInfo;
                UserService._isAdmin = parsedUser.role == this.userRoles.Admin;
                obsr.next(parsedUser);
              }, error: () => {
                UserService._checkRequest = undefined;
                obsr.next(undefined);
                UserService._isAdmin = false;
              }
            });
          }).pipe(shareReplay());

          return UserService._checkRequest;
        } else {
          UserService._isAdmin = false;
        }
      } else {
        UserService._isAdmin = parsedUser.role == this.userRoles.Admin;
        return new Observable<UserInfo>(obsr => obsr.next(parsedUser))
      }
    }

    return new Observable<UserInfo>(obsr => obsr.next(undefined));
  }

  public login(login: { userName: string, password: string }) {
    return this.mApiService.post<{ userName: string, password: string }, { token: string, refreshToken: string, expiration: string }>('/api/user/login', login, false);
  }

  public refreshToken(token: string) {
    if (UserService._refreshRequest) {
      return UserService._refreshRequest;
    }

    var q = UserService._refreshRequest = this.mApiService.post<{ accessToken: string, refreshToken: string }, { token: string, refreshToken: string }>('/api/user/refresh-token', {
      accessToken: this.getToken() ?? '',
      refreshToken: token
    }).pipe(shareReplay());

    q.subscribe({
      next: () => UserService._refreshRequest = undefined,
      error: () => UserService._refreshRequest = undefined,
    });

    return UserService._refreshRequest;
  }

  public isAdmin() {
    return UserService._isAdmin;
  }

  public isTokenExpired() {
    const user = localStorage.getItem(USER_KEY);
    if (user) {

      let parsedUser = JSON.parse(user) as UserInfo;
      return parsedUser.expiry * 1000 <= new Date().getTime();
    }
    return true;
  }
}
